import { isEqual } from 'lodash-es';

import type {
  CreateExploreRoundPayload,
  GenAiRequestHistoryRound,
} from '@/api/v4/genai-request-history.api';
import type { IOption } from '@/types';

import type { ExampleCompany } from '../explore.state';

export const mapRoundLocationsToOptions = (
  round: Pick<
    GenAiRequestHistoryRound,
    'locationContinent' | 'locationCountry'
  >,
): IOption[] => [
  ...round.locationContinent.map(continent => ({
    label: continent.shortDescription,
    value: JSON.stringify({ id: continent.id, type: 'continent' }),
  })),
  ...round.locationCountry.map(country => ({
    label: country.shortDescription,
    value: JSON.stringify({ id: country.id, type: 'country' }),
  })),
];

export const mapRoundCompaniesToExampleCompanies = (
  companies: GenAiRequestHistoryRound['companies'],
): ExampleCompany[] =>
  companies.map(company => ({
    id: company.crunchbaseId ?? company.organizationId ?? '',
    name: company.name ?? '',
    website: company.website ?? '',
    logoUrl: company.logoUrl ?? '',
  }));

const areSortedIdListsEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return sortedLeft.every((value, index) => value === sortedRight[index]);
};

export const buildCreateExploreRoundPayload = ({
  activeRoundNumber,
  confirmTruncate,
  defaultDescription,
  defaultExampleCompanies,
  defaultLocations,
  enableExploreAccountData,
  model,
  updatedDescription,
  updatedExampleCompanies,
  updatedLocations,
}: {
  activeRoundNumber: number;
  confirmTruncate?: boolean;
  defaultDescription: string;
  defaultExampleCompanies: ExampleCompany[];
  defaultLocations: IOption[];
  enableExploreAccountData: boolean;
  model: CreateExploreRoundPayload['model'];
  updatedDescription: string;
  updatedExampleCompanies: ExampleCompany[];
  updatedLocations: IOption[];
}): CreateExploreRoundPayload | null => {
  const promptChanged = updatedDescription !== defaultDescription;
  const locationsChanged = !isEqual(defaultLocations, updatedLocations);
  const companiesChanged = !areSortedIdListsEqual(
    defaultExampleCompanies.map(company => company.id),
    updatedExampleCompanies.map(company => company.id),
  );

  if (!promptChanged && !locationsChanged && !companiesChanged) {
    return null;
  }

  const payload: CreateExploreRoundPayload = {
    activeRoundNumber,
    enableExploreAccountData,
    model,
  };

  if (confirmTruncate) {
    payload.confirmTruncate = true;
  }

  if (promptChanged) {
    payload.prompt = updatedDescription;
  }

  if (locationsChanged) {
    payload.locations = updatedLocations.map(option =>
      JSON.parse(option.value),
    );
  }

  if (companiesChanged) {
    payload.companies = updatedExampleCompanies
      .filter(company => Boolean(company.id))
      .map(company => ({
        crunchbaseId: company.id,
        name: company.name,
        website: company.website,
        logoUrl: company.logoUrl,
      }));
  }

  return payload;
};

export const getExploreConflictCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const axiosError = error as {
    response?: {
      data?: { code?: string; message?: { code?: string } | string };
    };
  };
  const data = axiosError.response?.data;
  if (!data) {
    return null;
  }
  if (typeof data.code === 'string') {
    return data.code;
  }
  if (
    data.message &&
    typeof data.message === 'object' &&
    typeof data.message.code === 'string'
  ) {
    return data.message.code;
  }
  return null;
};
